import './styles.scss'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { waHTMLToText } from '@nixi1/nx-channel-utils'
import _clonedeep from 'lodash.clonedeep'

import { marketingAct } from 'redux/actions'
import { lang } from 'utils/helpers'
import { Button, SelectWithLabel } from 'components/Form'
import TemplateFreeTextVariable from './TemplateFreeTextVariable'
import TemplateAttachments from './TemplateAttachments'
import SelectFile from 'components/Buttons/SelectFile'

/**
 * @param {object} props
 * @param {string} props.channel
 * @param {string} props.provider
 * @param {string} props.channelToken
 * @param {string} props.channelId
 * @param {function} props.onChangeTemplate
 * @param {function} props.onChangeVariables
 * @param {function} props.onChangeFiles
 * @param {function} [props.onSend]
 * @param {function} [props.onCancel]
 * @param {string} [props.className]
 * @param {string} [props.templateId]
 * @param {string} [props.text]
 * @param {object} [props.conversationUser]
 */
const ConversationTemplateEditor = (props) => {
  const {
    conversationUser, channel, provider, channelToken, channelId,
    templateId, text, onChangeTemplate, onChangeVariables,
    onChangeFiles, onSend, onCancel, className
  } = props

  const dispatch = useDispatch()
  const mktTemplates = useSelector(store => store.marketing.templates)

  const templateKey = provider === 'whatsapp' ? `${provider}_${channel}` : channel
  const templateVars = mktTemplates.variables || []
  const templates = useMemo(() => {
    return mktTemplates?.[templateKey] || []
  }, [mktTemplates, templateKey])

  const templatesOptions = useMemo(() => {
    return templates.map(companyTemplate => ({
      value: companyTemplate._id,
      label: companyTemplate.name
    })) || []
  }, [templates])

  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selectedTemplateVariables, setSelectedTemplateVariables] = useState([])
  const [selectedTemplateFiles, setSelectedTemplateFiles] = useState([])

  useEffect( () => {
    let isSubscribed = true

    if (isSubscribed) handleSelectTemplate()

    return () => isSubscribed = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, templateId])

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) Promise.all([
      dispatch(marketingAct.getTemplateVariables()),
      dispatch(marketingAct.getChatTemplatesByChannel({
        channel, provider, channelId,
        token: channelToken
      }))
    ])

    return () => isSubscribed = false
  }, [dispatch, channel, provider, channelToken, channelId])

  const resetEditor = () => {
    setSelectedTemplate(null)
    setSelectedTemplateVariables([])
    setSelectedTemplateFiles([])

    onChangeTemplate()
  }

  const handleSelectTemplate = (tmpId) => {
    const id = tmpId || templateId
    if (!selectedTemplate && !id) return

    if (!id) {
      resetEditor()

      return
    }

    const template = _clonedeep(templates.find(template => template._id === id))
    if (!template) {
      resetEditor()
      return null
    }

    if (selectedTemplate && selectedTemplate._id === template._id) return

    setSelectedTemplate(template)
    setSelectedTemplateVariables(template?.variables || [])
    setSelectedTemplateFiles(template?.attachments || [])

    onChangeTemplate(template)
  }

  const handleFreeTextVariables = ({ index, value }) => {
    const updatedVariableValues = [...selectedTemplateVariables]
    updatedVariableValues[index] = { ...updatedVariableValues[index], value }

    setSelectedTemplateVariables(updatedVariableValues)
    onChangeVariables(updatedVariableValues)
  }

  const handleFiles = files => {
    setSelectedTemplateFiles(files)
    onChangeFiles(files)
  }

  const handleDisableSend = () => {
    const template = templates.find(template => template._id === selectedTemplate?._id)
    if (!template) return true

    if (selectedTemplate?.variables?.some(variable => !variable.field)) {
      if (!selectedTemplateVariables?.some(variable => variable?.value)) return true
    }

    return false
  }

  const hasFreeTextVariables = () => {
    if (selectedTemplate) {
      if (selectedTemplate.variables.some(variable => !variable.field)) return true
    }

    return false
  }

  return (
    <div className={`conversation-template-editor ${className || ''}`}>
      <div className='conversation-template-editor__section-row'>
        <div className='conversation-template-editor__section-column conversation-template-editor__section-column--template'>
          <span className='conversation-template-editor__section-title'>{lang('dashboard_template')}</span>
          <SelectWithLabel
            classNameWrapper='conversation-template-editor__templates-selector'
            placeholder={lang('dashboard_template')}
            options={templatesOptions}
            value={selectedTemplate ? { value: selectedTemplate._id, label: selectedTemplate.name } : null }
            onChange={e => handleSelectTemplate(e.value)}
          />
        </div>
        {hasFreeTextVariables() ? (
          <div className='conversation-template-editor__section-column conversation-template-editor__section-column--variables'>
            <span className='conversation-template-editor__section-title'>{lang('dashboard_variables')}</span>
            <div className='conversation-template-editor__variables-input-container'>
              <TemplateFreeTextVariable
                template={selectedTemplate}
                templateVars={templateVars}
                selectedTemplateVariables={selectedTemplateVariables}
                handleFreeTextVariables={handleFreeTextVariables}
              />
            </div>
          </div>
        ) : null}
        <div className='conversation-template-editor__section-column conversation-template-editor__section-column--preview'>
          <span className='conversation-template-editor__section-title'>{lang('dashboard_preview')}</span>
          <textarea
            disabled
            className='conversation-template-editor__preview-input'
            value={textToDisplay(text, selectedTemplateVariables)}
          />
        </div>
      </div>
      {(selectedTemplate?.header?.type === 'media' && selectedTemplateFiles?.length) ? (
        <div
          className='conversation-template-editor__section-row'
          style={{
            marginLeft: hasFreeTextVariables() ? '145px' : null
          }}
        >
          <div className='conversation-template-editor__section-column'>
            <SelectFile
              user={conversationUser}
              iconName='icon-upload-cloud-primary-dark'
              title={lang('dashboard_change-attached')}
              accept={`${selectedTemplateFiles?.[0].mimeType}/*`}
              onChange={handleFiles}
            />
          </div>
          <div className='conversation-template-editor__section-column'>
            <div className='conversation-template-editor__attachment-container'>
              <TemplateAttachments
                selectedTemplateFiles={selectedTemplateFiles}
                selectedTemplate={templates.find(template => template._id === selectedTemplate?.value)}
                handleFiles={handleFiles}
              />
            </div>
          </div>
        </div>
      ) : null}
      <div
        className='conversation-template-editor__section-row'
        style={{
          marginLeft: hasFreeTextVariables() ? '290px' : '145px'
        }}
      >
        <div className='conversation-template-editor__section-column'>
          <div className='conversation-template-editor__preview-buttons-container'>
            {typeof onSend ==='function' ? (
              <Button
                primary
                value={lang('dashboard_send')}
                disabled={handleDisableSend()}
                onClick={onSend}
              />
            ) : null}
            {typeof onCancel === 'function' ? (
              <Button
                clearPrimary
                value={lang('dashboard_cancel')}
                onClick={onCancel}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function textToDisplay(text = '', variables = []) {
  let newText = waHTMLToText.replace(text)
  variables.forEach(e => {
    if (e) {
      newText = newText.replace(e.name, e.value || e.name)
    }
  })
  return newText
}

export default ConversationTemplateEditor
